import React, { useState } from 'react';
import { X, Info, ArrowRight } from 'lucide-react';

interface WelcomePopupProps {
  onViewProcess: () => void;
}

const WELCOME_POPUP_KEY = 'scalesmall_welcome_popup_seen';

export default function WelcomePopup({ onViewProcess }: WelcomePopupProps) {
  const [isVisible, setIsVisible] = useState(() => (
    typeof window !== 'undefined' && !window.localStorage.getItem(WELCOME_POPUP_KEY)
  ));

  const handleClose = () => {
    localStorage.setItem(WELCOME_POPUP_KEY, 'true');
    setIsVisible(false);
  };

  const handleViewProcess = () => {
    handleClose();
    onViewProcess();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full transform transition-all animate-slideUp">
        <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-xl px-6 py-8">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
            aria-label="Close popup"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="bg-white bg-opacity-20 rounded-full p-3">
              <Info className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-bold">Welcome to ScaleSmall!</h2>
          </div>
          <p className="text-blue-50 text-sm ml-14">
            Let's get you started on the right path
          </p>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-700 text-base leading-relaxed">
            Before you begin, we highly recommend reviewing our <strong>two-phase implementation process</strong> to understand how we'll work together to scale your business.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-blue-900 text-sm font-medium">
              Understanding the process will help you prepare the necessary information and ensure a smooth onboarding experience.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={handleViewProcess}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 group"
            >
              View Implementation Process
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleClose}
              className="sm:flex-none bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              I'll Start Now
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2">
            You can always access the implementation process guide using the button at the top of the page.
          </p>
        </div>
      </div>
    </div>
  );
}
